/**
 * Product bundle: add upgrade product with main ATC.
 * Cart/checkout discounts come from Shopify Automatic discounts (Buy X Get Y).
 *
 * click_mode:
 * - checkbox: toggle selection, add with main product form submit
 * - add_item: button adds only the clicked bundle product
 * - add_both: button adds clicked product + current product page product
 */
class ProductBundle extends HTMLElement {
  connectedCallback() {
    this.form = document.getElementById(this.dataset.formId);
    this.onSubmit = this.onSubmit.bind(this);
    this.onSelectChange = this.onSelectChange.bind(this);
    this.onAddClick = this.onAddClick.bind(this);

    this.querySelectorAll('[data-bundle-select]').forEach((select) => {
      select.addEventListener('change', this.onSelectChange);
    });

    this.querySelectorAll('[data-bundle-add]').forEach((button) => {
      button.addEventListener('click', this.onAddClick);
    });

    if (this.clickMode === 'checkbox' && this.form) {
      this.form.addEventListener('submit', this.onSubmit, { capture: true });
    }
  }

  disconnectedCallback() {
    this.querySelectorAll('[data-bundle-select]').forEach((select) => {
      select.removeEventListener('change', this.onSelectChange);
    });

    this.querySelectorAll('[data-bundle-add]').forEach((button) => {
      button.removeEventListener('click', this.onAddClick);
    });

    if (this.form) {
      this.form.removeEventListener('submit', this.onSubmit, { capture: true });
    }
  }

  get clickMode() {
    return this.dataset.clickMode || 'checkbox';
  }

  get selectedBundles() {
    return Array.from(this.querySelectorAll('input[name="bundles"]:checked:not(:disabled)'));
  }

  onSelectChange(event) {
    const select = event.currentTarget;
    const option = select.options[select.selectedIndex];
    if (!option) return;

    const checkbox = document.getElementById(select.dataset.target);
    const addButton = select.closest('.product-bundle__card')?.querySelector('[data-bundle-add]');
    const image = document.getElementById(select.dataset.imageTarget);
    const item = select.closest('.product-bundle__card');
    const priceWrap = item?.querySelector('[data-bundle-price]');

    if (checkbox) {
      checkbox.value = option.value;
      checkbox.dataset.originalPrice = option.dataset.originalPrice || '';
      checkbox.dataset.discountedPrice = option.dataset.discountedPrice || '';
      checkbox.disabled = option.disabled;
    }

    if (addButton) {
      addButton.dataset.variantId = option.value;
      addButton.dataset.originalPrice = option.dataset.originalPrice || '';
      addButton.dataset.discountedPrice = option.dataset.discountedPrice || '';
      addButton.disabled = option.disabled;
    }

    if (image && option.dataset.img) {
      image.src = option.dataset.img;
    }

    if (priceWrap) {
      const saleEl = priceWrap.querySelector('[data-price-sale]');
      const compareEl = priceWrap.querySelector('[data-price-compare]');
      const original = Number(option.dataset.originalPrice || 0);
      const discounted = Number(option.dataset.discountedPrice || original);
      const textClass = saleEl?.className.includes('h6') ? 'h6 ' : '';

      if (discounted < original) {
        if (compareEl) {
          compareEl.hidden = false;
          compareEl.textContent = option.dataset.compareLabel || compareEl.textContent;
        } else {
          const compare = document.createElement('compare-at-price');
          compare.className = `${textClass}text-subdued line-through`;
          compare.dataset.priceCompare = '';
          compare.textContent = option.dataset.compareLabel || '';
          priceWrap.prepend(compare);
        }

        if (saleEl) {
          saleEl.className = `${textClass}text-on-sale`;
          saleEl.textContent = option.dataset.priceLabel || saleEl.textContent;
        }
      } else {
        if (compareEl) compareEl.hidden = true;
        if (saleEl) {
          saleEl.className = `${textClass}text-subdued`;
          saleEl.textContent = option.dataset.compareLabel || option.dataset.priceLabel || saleEl.textContent;
        }
      }
    }
  }

  async onAddClick(event) {
    const button = event.currentTarget;
    if (button.disabled || button.getAttribute('aria-busy') === 'true') return;

    const mode = this.clickMode;
    if (mode === 'checkbox') return;

    const variantId = Number(button.dataset.variantId);
    if (!variantId) return;

    const items = [];

    if (mode === 'add_both') {
      if (!this.form?.checkValidity()) {
        this.form.reportValidity();
        return;
      }

      const formData = new FormData(this.form);
      const parentId = Number(formData.get('id'));
      const parentQuantity = Number(formData.get('quantity') || 1);

      if (!parentId) return;

      items.push({
        id: parentId,
        quantity: parentQuantity,
        properties: this.extractProperties(formData),
      });
    }

    items.push({
      id: variantId,
      quantity: 1,
    });

    await this.addItemsToCart(items, {
      busyTarget: button,
      sectionsUrlVariantId: mode === 'add_both' && this.form ? this.form.id?.value || variantId : variantId,
    });
  }

  async onSubmit(event) {
    if (this.clickMode !== 'checkbox') return;

    const bundles = this.selectedBundles;
    if (!bundles.length) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    if (!this.form?.checkValidity()) {
      this.form.reportValidity();
      return;
    }

    const formData = new FormData(this.form);
    const parentId = formData.get('id');
    const parentQuantity = Number(formData.get('quantity') || 1);

    const items = [
      {
        id: Number(parentId),
        quantity: parentQuantity,
        properties: this.extractProperties(formData),
      },
    ];

    bundles.forEach((input) => {
      items.push({
        id: Number(input.value),
        quantity: 1,
      });
    });

    const submitButtons = Array.from(this.form.elements).filter((el) => el.type === 'submit');

    await this.addItemsToCart(items, {
      busyTargets: submitButtons,
      sectionsUrlVariantId: parentId,
      onSuccess: () => {
        this.selectedBundles.forEach((input) => {
          input.checked = false;
        });
      },
      dispatchFormEvents: true,
    });
  }

  async addItemsToCart(items, options = {}) {
    const {
      busyTarget,
      busyTargets = busyTarget ? [busyTarget] : [],
      sectionsUrlVariantId,
      onSuccess,
      dispatchFormEvents = true,
    } = options;

    busyTargets.forEach((button) => button.setAttribute('aria-busy', 'true'));
    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

    try {
      let sectionsToBundle = [];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:prepare-bundled-sections', {
          bubbles: true,
          detail: { sections: sectionsToBundle },
        })
      );

      const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          items,
          sections: sectionsToBundle.join(','),
          sections_url: `${Shopify.routes.root}variants/${sectionsUrlVariantId}`,
        }),
      });

      const responseJson = await response.json();

      if (!response.ok) {
        this.form?.dispatchEvent(
          new CustomEvent('cart:error', {
            bubbles: true,
            detail: { error: responseJson.description || responseJson.message },
          })
        );
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        return;
      }

      const cartContent = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
      cartContent.sections = responseJson.sections;

      if (window.themeVariables?.settings?.cartType === 'page' || window.themeVariables?.settings?.pageType === 'cart') {
        window.location.href = `${Shopify.routes.root}cart`;
        return;
      }

      if (dispatchFormEvents && this.form) {
        this.form.dispatchEvent(
          new CustomEvent('variant:add', {
            bubbles: true,
            detail: {
              items: responseJson.items || [responseJson],
              cart: cartContent,
            },
          })
        );
      }

      document.documentElement.dispatchEvent(
        new CustomEvent('cart:change', {
          bubbles: true,
          detail: {
            baseEvent: 'variant:add',
            cart: cartContent,
          },
        })
      );

      onSuccess?.();
    } catch (error) {
      console.error(error);
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    } finally {
      busyTargets.forEach((button) => button.removeAttribute('aria-busy'));
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
    }
  }

  extractProperties(formData) {
    const properties = {};
    for (const [key, value] of formData.entries()) {
      const match = key.match(/^properties\[(.+)\]$/);
      if (match && value !== '') {
        properties[match[1]] = value;
      }
    }
    return properties;
  }
}

if (!customElements.get('product-bundle')) {
  customElements.define('product-bundle', ProductBundle);
}
